import { Resources } from 'constants/resources';

import { useCallback, useEffect, useState } from 'react';

import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify, useRecordContext } from 'react-admin';
import { Switch } from 'shared/mui/Switch';
import { SwitchProps } from 'shared/mui/Switch/Switch';
import { Tooltip } from 'shared/mui/Tooltip';
import { TypographyWithBold } from 'shared/mui/Typography';
import { GatewayRule } from 'types/embedded/gatewayRule';

import { GatewayRuleActiveSwitchConfirm } from './GatewayRuleActiveSwitchConfirm';
import { useUpdateActiveRule } from './hooks/useUpdateActiveRule';

type Props = {
  size?: 'small' | 'medium';
  onSuccess?: () => void;
} & Pick<SwitchProps, 'sx'>;

export const GatewayRuleActiveSwitch: React.FC<Props> = ({
  size = 'medium',
  onSuccess,
  sx = { marginRight: '16px' },
}) => {
  const translate = useTypedTranslate();
  const { handleOpenRecordModal, handleCloseRecordModal, openRecordModal } = useRecordModal();
  const notify = useNotify();
  const record = useRecordContext<GatewayRule>();
  const [isActive, setIsActive] = useState(record?.active);

  const defaultOnSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: [Resources.GatewayRules] });
  };

  const { updateActiveRule } = useUpdateActiveRule({
    onSuccess: () => {
      onSuccess ? onSuccess() : defaultOnSuccess();
      setIsActive(!isActive);
      notify(
        <TypographyWithBold
          text={translate(
            isActive ? 'app.gatewayRules.ruleDisabled' : 'app.gatewayRules.ruleEnabled',
            { name: record?.name || '' },
          )}
        />,
        { type: 'success' },
      );
    },
    onError: (error: any) => {
      notify(
        `${translate('app.common.error')}: ${error}` ||
          translate('app.gatewayRules.activityChangeError'),
        { type: 'error' },
      );
    },
  });

  const onConfirm = useCallback(
    () => updateActiveRule({ id: record?.['@id'], isActive: !isActive }),
    [record, isActive],
  );

  const toggleActiveGatewayRule = useCallback(() => {
    handleOpenRecordModal(record);
  }, [record]);

  useEffect(() => {
    setIsActive(record?.active);
  }, [record]);

  return (
    <>
      <GatewayRuleActiveSwitchConfirm
        onClose={handleCloseRecordModal}
        onConfirm={onConfirm}
        open={openRecordModal}
        record={record}
      />
      <Tooltip
        arrow
        placement="top"
        title={
          isActive
            ? translate('app.gatewayRules.ruleIsActive')
            : translate('app.gatewayRules.ruleIsInactive')
        }
      >
        <Switch checked={isActive} onClick={toggleActiveGatewayRule} size={size} sx={sx} />
      </Tooltip>
    </>
  );
};
